import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Phone, Mail, Linkedin, MessageCircle, Trash2, ArrowLeft, Wallet, Plus, Check, Video, Headphones } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useLanguage } from "../../context/LanguageContext";
import {
  tCrm,
  crmApi,
  formatCurrency,
  formatDate,
  STAGES,
  STAGE_COLORS,
  TEMPERATURE_META,
  TASK_TYPE_META,
  INDUSTRIES,
  COMPANY_SIZES,
  CURRENCIES,
  URGENCIES,
  LEAD_SOURCES,
  LOST_REASONS,
  logDevError,
} from "../../lib/crm";
import { toast } from "sonner";
import { useLeadFocus } from "../../context/LeadFocusContext";
import LeadQualification from "./LeadQualification";

export default function CRMLeadDetail({ me }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const isAdmin = me.role === "admin";
  const { setLead: setFocusedLead } = useLeadFocus();

  const [lead, setLead] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("company");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await crmApi.get(`/leads/${id}`);
      setLead(r.data);
      setFocusedLead(r.data);
    } catch {
      navigate("/crm/leads");
    }
    loadActivity();
    loadTasks();
    if (isAdmin) crmApi.get("/agents").then((r) => setAgents(r.data)).catch((err) => logDevError("CRMLeadDetail.agents", err));
  }, [id, isAdmin, navigate, setFocusedLead]);

  const loadActivity = useCallback(() => {
    crmApi.get(`/leads/${id}/activity`).then((r) => setActivity(r.data)).catch((err) => logDevError("CRMLeadDetail.activity", err));
  }, [id]);

  const loadTasks = useCallback(() => {
    crmApi.get("/tasks", { params: { lead_id: id } }).then((r) => setTasks(r.data)).catch((err) => logDevError("CRMLeadDetail.tasks", err));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addTask = async ({ task_type, title, due_date, description }) => {
    try {
      await crmApi.post("/tasks", { task_type, title, due_date, description, lead_id: id });
      toast.success(t("tasks.created") || "✓");
      loadTasks();
    } catch (err) {
      logDevError("addTask", err);
      toast.error(t("common.error"));
    }
  };
  const toggleTask = async (task) => {
    try {
      await crmApi.put(`/tasks/${task.id}`, { status: task.status === "done" ? "pending" : "done" });
      loadTasks();
    } catch (err) {
      logDevError("toggleTask", err);
      toast.error(t("common.error"));
    }
  };
  const deleteTask = async (task) => {
    if (!window.confirm(t("tasks.deleteConfirm") || "Delete this task?")) return;
    try {
      await crmApi.delete(`/tasks/${task.id}`);
      toast.success(t("tasks.deleted") || "✓");
      loadTasks();
    } catch (err) {
      logDevError("deleteTask", err);
      toast.error(t("common.error"));
    }
  };

  const update = (field, value) => setLead((l) => ({ ...l, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      // Send only mutable fields (strip metadata)
      const payload = { ...lead };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.last_contact_date;
      delete payload.assigned_agent_id; // assignment via separate endpoint
      // Derived/computed fields — backend owns them
      delete payload.criteria_passed;
      delete payload.criteria_failed;
      delete payload.criteria_pending;
      delete payload.qualification_status;
      await crmApi.put(`/leads/${id}`, payload);
      toast.success(t("leadDetail.saved"));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "disqualified_reason_required") {
        toast.error(t("qual.reasonRequired"));
      } else {
        toast.error(typeof detail === "string" ? detail : t("common.error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const changeStage = async (newStage) => {
    try {
      await crmApi.put(`/leads/${id}`, { stage: newStage });
      toast.success("Stage updated");
      load();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const reassign = async (agentId, force = false) => {
    try {
      const res = await crmApi.put(`/leads/${id}/assign`, { agent_id: agentId || null, force });
      if (res.data?.no_op) {
        toast.info(t("assign.alreadyAssignedHere"));
      } else if (res.data?.reassigned) {
        toast.success(t("assign.reassignedOk"));
      } else {
        toast.success(t("assign.assignedOk"));
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "already_assigned") {
        const name = detail.current_agent?.full_name || detail.current_agent?.id || "—";
        if (window.confirm(`${t("assign.confirmReassignPrefix")} ${name}.\n\n${t("assign.confirmReassignSuffix")}`)) {
          // User confirmed force-reassign — retry with force=true
          await reassign(agentId, true);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : t("common.error"));
      }
    }
  };

  const remove = async () => {
    if (!window.confirm(t("leadDetail.confirmDelete"))) return;
    try {
      await crmApi.delete(`/leads/${id}`);
      toast.success("Deleted");
      navigate("/crm/leads");
    } catch {
      toast.error(t("common.error"));
    }
  };

  const [callResultOpen, setCallResultOpen] = useState(false);

  if (!lead) {
    return <div className="p-8 text-white/40">{t("common.loading")}</div>;
  }

  const phone = lead.contact_phone?.replace(/\D/g, "");
  const whatsappNumber = (lead.whatsapp || lead.contact_phone || "").replace(/\D/g, "");

  // --- Zoho deep-link helpers ---------------------------------------------
  // Region: Zoho hosts users either on .eu (European DC) or .com. GTX is NL-based
  // so we default to .eu — agents can override by adding ?region=com to the URL.
  const zohoRegion =
    new URLSearchParams(window.location.search).get("zoho_region") ||
    process.env.REACT_APP_ZOHO_REGION ||
    "eu";
  // Zoho Mail does NOT document a compose-with-prefill URL. Two strategies are used
  // in tandem so the agent always has the info ready:
  //   1. Open Zoho Mail compose (best-effort URL params — works on most accounts)
  //   2. ALSO copy the full email body to clipboard, so the agent can just paste
  //      if Zoho's compose ignores the URL params.
  // Same approach for Zoho Meetings (no documented prefill API).
  const ZOHO_MAIL_COMPOSE = `https://mail.zoho.${zohoRegion}/zm/#compose`;
  const ZOHO_MEET_SCHEDULE = `https://meeting.zoho.${zohoRegion}/#/meetings/schedule`;

  // Copy text to clipboard, swallowing errors silently if the browser blocks it.
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  // Lightweight backend audit so admin sees the action in the timeline
  const logExternal = (action, provider, target = "") => {
    crmApi
      .post(`/leads/${lead.id}/activity`, { action, provider, target })
      .then(() => loadActivity())
      .catch((err) => logDevError("logExternal", err));
  };

  // Build a rich, contextual email body using every lead field we have.
  const buildEmailBody = () => {
    const firstName = (lead.contact_full_name || "").split(" ")[0] || "";
    const serviceLine = lead.service_of_interest
      ? `\n\nVi que te interesa nuestro servicio de ${lead.service_of_interest}.`
      : "";
    return [
      `${t("zoho.emailGreeting")} ${firstName},`,
      "",
      `Te escribo desde Global Tech Xperts.${serviceLine}`,
      "",
      "Me gustaría agendar una breve llamada para entender mejor tus necesidades y ver cómo podemos ayudarte.",
      "",
      "¿Tendrías 15 minutos esta semana?",
      "",
      "Un saludo,",
      "[Tu nombre]",
      "Global Tech Xperts",
      "https://globaltechxperts.nl",
    ].join("\n");
  };

  const buildEmailSubject = () =>
    `${lead.company_name || "GTX"} · ${t("zoho.emailSubject")}`;

  // ─── Pipeline automation ───────────────────────────────────────────────
  // Pre-qualified stages in order — agent actions can only advance forward.
  const STAGE_ORDER = [
    "new",
    "contacting",
    "no_response",
    "call_back",
    "contacted",
    "meeting_scheduled",
    "qualified",
    "proposal_sent",
    "negotiation",
    "won",
    "not_interested",
  ];
  const stageIndex = (s) => STAGE_ORDER.indexOf(s);

  const setStage = async (target, silent = false) => {
    if (!lead || lead.stage === target) return;
    try {
      const r = await crmApi.put(`/leads/${lead.id}`, { stage: target });
      setLead(r.data);
      if (!silent) toast.success(`→ ${t(`stage.${target}`)}`);
      loadActivity();
    } catch (err) {
      logDevError("setStage", err);
    }
  };

  // Only advance if the lead is currently in an EARLIER stage (no regression).
  const bumpStageIfBefore = async (target, silent = false) => {
    if (!lead) return;
    const here = stageIndex(lead.stage);
    const dest = stageIndex(target);
    if (dest === -1) return;
    if (here !== -1 && here < dest) {
      await setStage(target, silent);
    }
  };

  const onCallClick = () => {
    logExternal("external_call", "Phone", lead.contact_phone);
    // Mark first contact attempt as "contacting" while the agent calls
    bumpStageIfBefore("contacting", true);
    // Show the result dialog after the agent returns from the call (slight delay so the dialer opens first)
    setTimeout(() => setCallResultOpen(true), 500);
  };

  const onCallResult = async (outcome) => {
    setCallResultOpen(false);
    // outcome: "contacted" | "no_response" | "call_back" | "meeting_scheduled" | "not_interested"
    if (!outcome) return;
    await setStage(outcome);
    crmApi
      .post(`/leads/${lead.id}/activity`, {
        action: "external_call",
        provider: "Phone",
        target: lead.contact_phone,
        note: `Outcome: ${outcome}`,
      })
      .then(() => loadActivity())
      .catch((err) => logDevError("call outcome", err));
  };

  const onEmailLike = (action, provider, target) => {
    logExternal(action, provider, target);
    // Sending any outreach communication = the lead is being worked on
    bumpStageIfBefore("contacting", true);
  };

  const onScheduleMeeting = () => {
    logExternal("external_meeting", "Zoho Meetings", lead.contact_email || lead.contact_full_name || lead.company_name);
    // Booking a meeting is a strong signal — promote up to meeting_scheduled
    bumpStageIfBefore("meeting_scheduled");
  };

  const openZohoMail = async () => {
    if (!lead.contact_email) return;
    const subject = buildEmailSubject();
    const body = buildEmailBody();
    // Use the standard mailto: protocol — RFC-compliant and pre-fills To /
    // Subject / Body in every mail client. If the agent has set Zoho Mail as
    // the default mailto handler in Chrome (chrome://settings/handlers), this
    // opens Zoho's compose window directly with all fields populated.
    const mailto = `mailto:${encodeURIComponent(lead.contact_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Trigger the mailto handler via a transient anchor click. This is more
    // reliable than window.location.href and won't navigate the SPA away.
    const a = document.createElement("a");
    a.href = mailto;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Always copy a ready-to-paste block as a safety net (in case the OS
    // mail handler is not set or the click is blocked by the browser).
    const ok = await copyToClipboard(
      `To: ${lead.contact_email}\nSubject: ${subject}\n\n${body}`
    );
    toast.success(
      ok
        ? t("zoho.mailCopiedAndOpened")
        : t("zoho.mailOpenedNoCopy")
    );
    onEmailLike("external_email", "Zoho Mail", lead.contact_email);
  };

  const openZohoMeeting = async () => {
    const title = `${lead.company_name || "GTX"} · Sales call`;
    const attendee = lead.contact_email || lead.contact_full_name || lead.company_name;
    const url = ZOHO_MEET_SCHEDULE;
    window.open(url, "_blank", "noopener,noreferrer");
    // Copy a meeting-ready snippet for fast paste-and-go
    const ok = await copyToClipboard(
      `Title: ${title}\nAttendees: ${attendee || ""}\nAgenda:\n  • Intro & GTX presentation\n  • ${lead.company_name || "Client"} needs\n  • Next steps`
    );
    toast.success(
      ok
        ? t("zoho.meetCopiedAndOpened")
        : t("zoho.meetOpenedNoCopy")
    );
    onScheduleMeeting();
  };

  // Open the GTX Call Assistant in a new tab, prefilled with this lead's data.
  // The static HTML reads URL params via a small inline script we inject below.
  const openCallAssistant = () => {
    const params = new URLSearchParams({
      lead_id: lead.id || "",
      empresa: lead.company_name || "",
      nombre: lead.contact_full_name || "",
      cargo: lead.contact_title || "",
      email: lead.contact_email || "",
      telefono: lead.contact_phone || lead.whatsapp || "",
      pais: lead.country || "",
      web: lead.website || "",
      producto: lead.service_of_interest || "",
      stage: lead.stage || "",
      agent: me?.full_name || "",
      // Optional: prefer Variante 8 (segundo intento) when lead has prior no-response stage
      variant: lead.stage === "no_response" ? "8" : (lead.stage === "call_back" ? "10" : (lead.stage === "new" ? "1" : "")),
    });
    window.open(`/call-assistant.html?${params.toString()}`, "_blank", "noopener,noreferrer");
    logExternal("open_call_assistant", "Call Assistant", lead.contact_full_name || lead.company_name);
  };

  const tabs = [
    { id: "company", label: t("leadDetail.company") },
    { id: "contact", label: t("leadDetail.contact") },
    { id: "qualification", label: t("qual.tab") },
    { id: "commercial", label: t("leadDetail.commercial") },
    { id: "internal", label: t("leadDetail.internal") },
    { id: "tasks", label: `${t("leadDetail.tasks")} (${tasks.length})` },
    { id: "activity", label: t("leadDetail.activity") },
  ];

  const scheduleClosing = async () => {
    // Move lead to the next pipeline stage (qualified → meeting_scheduled if not yet,
    // otherwise leave stage but ensure it's at least 'qualified').
    try {
      const targetStage = lead.stage === "qualified" ? "meeting_scheduled" : "qualified";
      await crmApi.put(`/leads/${id}`, { stage: targetStage });
      toast.success(t("qual.scheduleClosing"));
      load();
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/crm/leads" className="text-sm text-white/50 hover:text-white flex items-center gap-2" data-testid="back-to-leads">
          <ArrowLeft className="w-4 h-4" /> {t("leadDetail.back")}
        </Link>
        {isAdmin && (
          <button
            onClick={remove}
            data-testid="lead-delete-btn"
            className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
          >
            <Trash2 className="w-3.5 h-3.5" /> {t("leadDetail.delete")}
          </button>
        )}
      </div>

      {/* Header card */}
      <div className="glass-strong rounded-2xl p-6 border border-white/8 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Input
              value={lead.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              data-testid="lead-company-name"
              className="bg-transparent border-0 text-2xl font-bold text-white p-0 h-auto"
            />
            {lead.contact_full_name && (
              <div className="text-sm text-white/50 mt-1">{lead.contact_full_name} · {lead.contact_job_title || "—"}</div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={lead.stage}
              onChange={(e) => changeStage(e.target.value)}
              data-testid="lead-stage-select"
              className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2"
              style={{ borderColor: STAGE_COLORS[lead.stage] + "60" }}
            >
              {STAGES.map((s) => (
                <option key={s} value={s}>{t(`stage.${s}`)}</option>
              ))}
              <option value="lost">{t("stage.lost")}</option>
            </select>
            {isAdmin && (
              <select
                value={lead.assigned_agent_id || ""}
                onChange={(e) => reassign(e.target.value)}
                data-testid="lead-assign-select"
                className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">{t("leads.unassigned")}</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-white/8">
          <span className="text-[10px] uppercase tracking-wider text-white/40 mono self-center mr-2">
            {t("leadDetail.quickActions")}:
          </span>
          {whatsappNumber && (
            <a
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noreferrer"
              onClick={() => onEmailLike("external_whatsapp", "WhatsApp", whatsappNumber)}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20"
              data-testid="qa-whatsapp"
            >
              <MessageCircle className="w-3 h-3 inline mr-1" /> WhatsApp
            </a>
          )}
          {lead.contact_email && (
            <>
              <a
                href={`mailto:${lead.contact_email}`}
                onClick={() => onEmailLike("external_email", "Native mail", lead.contact_email)}
                className="text-xs px-3 py-1.5 rounded-md bg-lime-400/10 text-lime-300 border border-lime-400/30 hover:bg-lime-400/20"
                data-testid="qa-email"
              >
                <Mail className="w-3 h-3 inline mr-1" /> Email
              </a>
              <button
                type="button"
                onClick={openZohoMail}
                data-testid="qa-zoho-mail"
                className="text-xs px-3 py-1.5 rounded-md bg-orange-500/10 text-orange-300 border border-orange-500/30 hover:bg-orange-500/20"
                title={t("zoho.mailTooltip")}
              >
                <Mail className="w-3 h-3 inline mr-1" /> Zoho Mail
              </button>
            </>
          )}
          <button
            type="button"
            onClick={openZohoMeeting}
            data-testid="qa-zoho-meet"
            className="text-xs px-3 py-1.5 rounded-md bg-orange-500/10 text-orange-300 border border-orange-500/30 hover:bg-orange-500/20"
            title={t("zoho.meetTooltip")}
          >
            <Video className="w-3 h-3 inline mr-1" /> Zoho Meeting
          </button>
          {lead.contact_phone && (
            <a
              href={`tel:${lead.contact_phone}`}
              onClick={onCallClick}
              className="text-xs px-3 py-1.5 rounded-md bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/20"
              data-testid="qa-call"
            >
              <Phone className="w-3 h-3 inline mr-1" /> Call
            </a>
          )}
          <button
            type="button"
            onClick={openCallAssistant}
            data-testid="qa-call-assistant"
            className="text-xs px-3 py-1.5 rounded-md bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/40 hover:bg-fuchsia-500/25 font-semibold"
            title={t("assistant.tooltip")}
          >
            <Headphones className="w-3 h-3 inline mr-1" /> {t("assistant.btn")}
          </button>
          {lead.contact_linkedin && (
            <a href={lead.contact_linkedin} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-md bg-sky-500/10 text-sky-300 border border-sky-500/30 hover:bg-sky-500/20">
              <Linkedin className="w-3 h-3 inline mr-1" /> LinkedIn
            </a>
          )}
        </div>
      </div>

      {/* Won lead → create commission draft (admin only) */}
      {isAdmin && lead.stage === "won" && (
        <WonLeadBanner lead={lead} t={t} onCreated={load} />
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-white/8">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`tab-${tb.id}`}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition border-b-2 ${
              tab === tb.id ? "border-lime-400 text-white" : "border-transparent text-white/40 hover:text-white/80"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {tab === "company" && (
          <>
            <TF label="Website" value={lead.website} onChange={(v) => update("website", v)} />
            <TF label="Industry" value={lead.industry} onChange={(v) => update("industry", v)} options={INDUSTRIES} />
            <TF label="Category" value={lead.category} onChange={(v) => update("category", v)} />
            <TF label="Priority" value={lead.priority_score} onChange={(v) => update("priority_score", v)} options={["HIGH", "MEDIUM", "LOW"]} />
            <TF label="Score (0-5)" type="number" value={lead.priority_numeric} onChange={(v) => update("priority_numeric", parseFloat(v) || 0)} />
            <TF label="Rating" value={lead.rating} onChange={(v) => update("rating", v)} />
            <TF label="Company size" value={lead.company_size} onChange={(v) => update("company_size", v)} options={COMPANY_SIZES} />
            <TF label="Estimated revenue" type="number" value={lead.estimated_revenue} onChange={(v) => update("estimated_revenue", parseFloat(v) || 0)} />
            <TF label="Country" value={lead.country} onChange={(v) => update("country", v)} />
            <TF label="City" value={lead.city} onChange={(v) => update("city", v)} />
            <TF label="City / Area" value={lead.city_area} onChange={(v) => update("city_area", v)} />
            <TF label="Full address" value={lead.full_address} onChange={(v) => update("full_address", v)} />
            <TF label="Tax ID" value={lead.tax_id} onChange={(v) => update("tax_id", v)} />
            <TF label="Company LinkedIn" value={lead.company_linkedin} onChange={(v) => update("company_linkedin", v)} />
            <TF label="Analysis / research notes" value={lead.analysis_notes} onChange={(v) => update("analysis_notes", v)} type="textarea" />
          </>
        )}

        {tab === "contact" && (
          <>
            <TF label="Full name" value={lead.contact_full_name} onChange={(v) => update("contact_full_name", v)} />
            <TF label="Job title" value={lead.contact_job_title} onChange={(v) => update("contact_job_title", v)} />
            <TF label="Email" value={lead.contact_email} onChange={(v) => update("contact_email", v)} />
            <TF label="All emails (; separated)" value={lead.all_emails} onChange={(v) => update("all_emails", v)} />
            <TF label="Phone" value={lead.contact_phone} onChange={(v) => update("contact_phone", v)} />
            <TF label="WhatsApp" value={lead.whatsapp} onChange={(v) => update("whatsapp", v)} />
            <TF label="LinkedIn" value={lead.contact_linkedin} onChange={(v) => update("contact_linkedin", v)} />
            <TF label="Department" value={lead.contact_department} onChange={(v) => update("contact_department", v)} />
            <TF label="Preferred language" value={lead.preferred_language} onChange={(v) => update("preferred_language", v)} options={["ES", "EN", "FR", "PT", "Other"]} />
            <TF label="Birthday" type="date" value={lead.birthday} onChange={(v) => update("birthday", v)} />
            <div className="flex items-center gap-3 col-span-2">
              <input
                type="checkbox"
                checked={!!lead.is_decision_maker}
                onChange={(e) => update("is_decision_maker", e.target.checked)}
                data-testid="lead-decision-maker"
                className="w-4 h-4"
              />
              <Label className="text-white/70">Is decision maker</Label>
            </div>
          </>
        )}

        {tab === "qualification" && (
          <LeadQualification
            lead={lead}
            update={update}
            onScheduleClosing={scheduleClosing}
          />
        )}

        {tab === "commercial" && (
          <>
            <TF label="Deal value" type="number" value={lead.deal_value} onChange={(v) => update("deal_value", parseFloat(v) || 0)} />
            <TF label="Currency" value={lead.currency} onChange={(v) => update("currency", v)} options={CURRENCIES} />
            <TF label="Expected close date" type="date" value={lead.expected_close_date} onChange={(v) => update("expected_close_date", v)} />
            <TF label="Service of interest" value={lead.service_of_interest} onChange={(v) => update("service_of_interest", v)} />
            <TF label="Client budget" type="number" value={lead.client_budget} onChange={(v) => update("client_budget", parseFloat(v) || 0)} />
            <TF label="Urgency" value={lead.urgency} onChange={(v) => update("urgency", v)} options={URGENCIES} />
            <TF label="Competition" value={lead.competition} onChange={(v) => update("competition", v)} />
            <TF label="Lead source" value={lead.lead_source} onChange={(v) => update("lead_source", v)} options={LEAD_SOURCES} />
            <TF label="Marketing campaign" value={lead.marketing_campaign} onChange={(v) => update("marketing_campaign", v)} />
            <TF label="Temperature" value={lead.temperature} onChange={(v) => update("temperature", v)} options={Object.keys(TEMPERATURE_META)} />
          </>
        )}

        {tab === "internal" && (
          <>
            <TF label="Next contact date" type="date" value={lead.next_contact_date} onChange={(v) => update("next_contact_date", v)} />
            {lead.stage === "lost" && (
              <TF label="Lost reason" value={lead.lost_reason} onChange={(v) => update("lost_reason", v)} options={LOST_REASONS} />
            )}
            <div className="col-span-2">
              <Label className="text-white/70 text-xs">Tags (comma separated)</Label>
              <Input
                value={(lead.tags || []).join(", ")}
                onChange={(e) => update("tags", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-white/70 text-xs">Internal notes</Label>
              <Textarea
                value={lead.internal_notes || ""}
                onChange={(e) => update("internal_notes", e.target.value)}
                data-testid="lead-internal-notes"
                className="bg-white/5 border-white/10 text-white mt-1 min-h-[120px]"
              />
            </div>
          </>
        )}

        {tab === "tasks" && (
          <div className="col-span-2 space-y-3">
            <NewTaskForm onAdd={addTask} t={t} />
            {tasks.length === 0 ? (
              <div className="text-white/40 text-sm py-4">{t("tasks.noTasks")}</div>
            ) : (
              tasks.map((tsk) => {
                const done = tsk.status === "done";
                return (
                  <div
                    key={tsk.id}
                    data-testid={`task-row-${tsk.id}`}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      done ? "bg-white/[0.02] border-white/5 opacity-70" : "bg-white/[0.03] border-white/8"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleTask(tsk)}
                        data-testid={`task-toggle-${tsk.id}`}
                        title={done ? t("tasks.markPending") : t("tasks.markDone")}
                        className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors ${
                          done
                            ? "bg-lime-400 border-lime-400 text-black"
                            : "bg-transparent border-white/30 hover:border-lime-400"
                        }`}
                      >
                        {done && <Check className="w-3 h-3" />}
                      </button>
                      <span className="text-xl">{TASK_TYPE_META[tsk.task_type]?.icon || "📝"}</span>
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${done ? "line-through text-white/40" : ""}`}>
                          {tsk.title}
                        </div>
                        <div className="text-[11px] text-white/40 mono">{formatDate(tsk.due_date)}</div>
                        {tsk.description && (
                          <div className="text-[11px] text-white/50 mt-0.5 truncate">{tsk.description}</div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteTask(tsk)}
                      data-testid={`task-del-${tsk.id}`}
                      className="text-white/40 hover:text-red-400 ml-2 shrink-0"
                      title={t("common.delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === "activity" && (
          <div className="col-span-2 space-y-2">
            {activity.length === 0 ? (
              <div className="text-white/40 text-sm">{t("leadDetail.noActivity")}</div>
            ) : (
              activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/8" data-testid={`activity-${a.id}`}>
                  <div className="w-2 h-2 rounded-full bg-lime-400 mt-2" />
                  <div className="flex-1">
                    <div className="text-sm">{a.description}</div>
                    <div className="text-[11px] text-white/40 mono mt-1">{formatDate(a.created_at)} · {a.actor_role}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Save bar */}
      {(tab === "company" || tab === "contact" || tab === "commercial" || tab === "internal" || tab === "qualification") && (
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="lead-save-btn" className="bg-lime-400 hover:bg-lime-300 text-white shadow-xl">
            {saving ? t("leadDetail.saving") : t("leadDetail.save")}
          </Button>
        </div>
      )}
      {callResultOpen && (
        <CallResultDialog
          contactName={lead.contact_full_name || lead.company_name}
          onPick={onCallResult}
          onClose={() => setCallResultOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}

function TF({ label, value, onChange, type = "text", options }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      {options ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
        >
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white/5 border-white/10 text-white mt-1"
        />
      )}
    </div>
  );
}


function WonLeadBanner({ lead, t, onCreated }) {
  const [busy, setBusy] = useState(false);
  // We only need the operations list to detect whether a commission was already created from this lead.
  const [hasCommission, setHasCommission] = useState(null);

  useEffect(() => {
    let alive = true;
    crmApi
      .get("/commissions")
      .then((r) => {
        if (!alive) return;
        setHasCommission((r.data || []).some((o) => o.lead_id === lead.id));
      })
      .catch(() => alive && setHasCommission(false));
    return () => {
      alive = false;
    };
  }, [lead.id]);

  const createDraft = async () => {
    setBusy(true);
    try {
      // Heuristic defaults: assume tech modality, 0 units → admin will edit before publishing.
      // Backend looks up the agent's latest supplier automatically.
      await crmApi.post(`/commissions/from-lead/${lead.id}`, {
        modality: "tech",
        notes: `From won lead: ${lead.company_name}`,
      });
      toast.success(t("comm.createdAsDraft"));
      setHasCommission(true);
      onCreated && onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "no_supplier_for_agent") {
        toast.error(t("comm.fromLeadNoSupplier"));
      } else {
        toast.error(typeof detail === "string" ? detail : t("common.error"));
      }
    } finally {
      setBusy(false);
    }
  };

  if (hasCommission) {
    return (
      <div
        data-testid="won-lead-banner-existing"
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-wrap items-center gap-3"
      >
        <Wallet className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-semibold text-emerald-200">
            {t("comm.title")} · {t("comm.draft")}/{t("comm.published")}
          </div>
          <div className="text-[11px] text-emerald-200/60 mt-0.5">
            <Link to="/crm/commissions" className="underline">
              {t("comm.tabOperations")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="won-lead-banner"
      className="rounded-xl border border-lime-400/30 bg-lime-400/5 p-4 flex flex-wrap items-center gap-3"
    >
      <Wallet className="w-5 h-5 text-lime-400 shrink-0" />
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold text-lime-200">{t("comm.fromLeadCta")}</div>
        <div className="text-[11px] text-lime-200/60 mt-0.5">{t("comm.fromLeadHint")}</div>
      </div>
      <Button
        onClick={createDraft}
        disabled={busy}
        data-testid="create-commission-from-lead-btn"
        className="bg-lime-400 hover:bg-lime-300 text-black font-semibold"
      >
        {busy ? t("common.saving") : t("comm.fromLeadCta")}
      </Button>
    </div>
  );
}


function NewTaskForm({ onAdd, t }) {
  // Default due-date: tomorrow 09:00 local
  const tomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    // Convert to local datetime-local string yyyy-MM-ddTHH:mm
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [form, setForm] = useState({
    task_type: "call",
    title: "",
    due_date: tomorrow(),
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await onAdd({
        task_type: form.task_type,
        title: form.title.trim(),
        due_date: new Date(form.due_date).toISOString(),
        description: form.description.trim(),
      });
      // reset
      setForm({ task_type: form.task_type, title: "", due_date: tomorrow(), description: "" });
      setExpanded(false);
    } finally {
      setBusy(false);
    }
  };

  const TASK_TYPES = [
    { value: "call", icon: "📞", label: t("tasks.typeCall") },
    { value: "email", icon: "✉️", label: t("tasks.typeEmail") },
    { value: "whatsapp", icon: "💬", label: t("tasks.typeWhatsapp") },
    { value: "meeting", icon: "📅", label: t("tasks.typeMeeting") },
    { value: "proposal", icon: "📄", label: t("tasks.typeProposal") },
    { value: "wait", icon: "⏳", label: t("tasks.typeWait") },
    { value: "note", icon: "📝", label: t("tasks.typeNote") },
  ];

  return (
    <form
      onSubmit={submit}
      data-testid="task-add-form"
      className="rounded-xl border border-lime-400/20 bg-lime-400/[0.04] p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <select
          value={form.task_type}
          onChange={(e) => setForm({ ...form, task_type: e.target.value })}
          data-testid="task-type-select"
          className="bg-zinc-950 border border-white/10 text-white text-sm rounded-md px-2 py-2 w-24 shrink-0"
        >
          {TASK_TYPES.map((tt) => (
            <option key={tt.value} value={tt.value}>{tt.icon} {tt.label}</option>
          ))}
        </select>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          onFocus={() => setExpanded(true)}
          placeholder={t("tasks.titlePh")}
          data-testid="task-title-input"
          required
          className="bg-zinc-950 border-white/10 text-white flex-1"
        />
        <Button
          type="submit"
          size="sm"
          disabled={busy || !form.title.trim()}
          data-testid="task-add-submit"
          className="bg-lime-400 hover:bg-lime-300 text-black font-semibold shrink-0"
        >
          <Plus className="w-4 h-4 mr-1" />{t("tasks.addBtn")}
        </Button>
      </div>
      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-wider">{t("tasks.dueDate")}</Label>
            <Input
              type="datetime-local"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="task-due-input"
              className="bg-zinc-950 border-white/10 text-white text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-wider">{t("tasks.descriptionOpt")}</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("tasks.descriptionPh")}
              data-testid="task-desc-input"
              className="bg-zinc-950 border-white/10 text-white text-sm mt-1"
            />
          </div>
        </div>
      )}
    </form>
  );
}


function CallResultDialog({ contactName, onPick, onClose, t }) {
  const OUTCOMES = [
    { key: "contacted",         icon: "✅", label: t("call.r_contacted"),         tone: "lime" },
    { key: "meeting_scheduled", icon: "📅", label: t("call.r_meetingScheduled"), tone: "violet" },
    { key: "call_back",         icon: "↩️", label: t("call.r_callBack"),         tone: "amber" },
    { key: "no_response",       icon: "📵", label: t("call.r_noResponse"),       tone: "zinc" },
    { key: "not_interested",    icon: "🚫", label: t("call.r_notInterested"),    tone: "red" },
  ];
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="call-result-dialog"
    >
      <div
        className="glass-strong rounded-2xl p-6 w-full max-w-md border border-indigo-400/40 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center text-xl shrink-0">
            📞
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white" data-testid="call-result-title">
              {t("call.resultTitle")}
            </h2>
            <p className="text-xs text-white/50 truncate">{contactName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white shrink-0"
            data-testid="call-result-cancel"
          >
            ✕
          </button>
        </div>
        <div className="space-y-2">
          {OUTCOMES.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => onPick(o.key)}
              data-testid={`call-outcome-${o.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border text-left transition-colors
                ${
                  o.tone === "lime"
                    ? "border-lime-400/30 hover:bg-lime-400/10 hover:border-lime-400/60"
                    : o.tone === "violet"
                    ? "border-violet-400/30 hover:bg-violet-400/10 hover:border-violet-400/60"
                    : o.tone === "amber"
                    ? "border-amber-400/30 hover:bg-amber-400/10 hover:border-amber-400/60"
                    : o.tone === "zinc"
                    ? "border-zinc-500/30 hover:bg-zinc-500/10 hover:border-zinc-500/60"
                    : "border-red-500/30 hover:bg-red-500/10 hover:border-red-500/60"
                }`}
            >
              <span className="text-xl">{o.icon}</span>
              <span className="text-sm text-white">{o.label}</span>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-white/40 text-center uppercase tracking-wider mt-1">
          {t("call.hint")}
        </p>
      </div>
    </div>
  );
}
